import React, { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

/**
 * InstallAppPrompt
 * ----------------
 * Shows a Fortnite-styled bottom banner on mobile that:
 *   • Fires the native Chrome/Android beforeinstallprompt when available.
 *   • Falls back to an iOS Safari "Share → Add to Home Screen" tutorial
 *     (Apple never fires beforeinstallprompt).
 * Auto-hides once installed, dismissed, or on desktop.
 */
const DISMISS_KEY = 'sv_pwa_install_dismissed_at';
const DISMISS_MS = 1000 * 60 * 60 * 24 * 7; // remember dismiss for 7 days

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isMobile() {
  const ua = navigator.userAgent || '';
  if (/android|iphone|ipad|ipod|mobile/i.test(ua)) return true;
  // Fallback for headless / spoofed desktop UA on a phone-sized viewport
  // (also lets the banner appear when a user resizes their browser small).
  return (window.innerWidth || 0) < 768 && (navigator.maxTouchPoints || 0) >= 0
    ? window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 640
    : false;
}

function isIOS() {
  const ua = navigator.userAgent || '';
  return /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
}

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - parseInt(raw, 10) < DISMISS_MS;
  } catch {
    return false;
  }
}

export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobile() || wasDismissedRecently()) return () => {};

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari never fires beforeinstallprompt — show the banner after
    // a short delay so it doesn't jump in on first paint.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setVisible(true), 3500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch { /* ignore */ }
      setDeferred(null);
      setVisible(false);
    } else if (isIOS()) {
      setShowIOSHelp(true);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    setShowIOSHelp(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="pwa-install-prompt"
        className="fixed left-2 right-2 z-[80] pointer-events-auto sm:hidden"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
        }}
      >
        <div
          className="mx-auto max-w-md rounded-2xl border-[3px] border-[#ffd900] shadow-[0_6px_0_#ffd900,0_0_40px_rgba(255,214,0,0.35)] overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, #3a1064 0%, #1a0440 55%, #0b0320 100%)',
          }}
        >
          <div className="flex items-center gap-3 p-3">
            <img
              src="/icons/icon-192.png"
              alt="Sprite Vault"
              className="h-14 w-14 rounded-xl border-2 border-[#ffd900] shadow-[0_3px_0_#ffd900]"
            />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-[2px] text-[#ffd900]">
                Install the App
              </div>
              <div className="text-white font-black leading-tight text-sm">
                Add Sprite Vault to your Home Screen
              </div>
              <div className="text-white/70 text-[11px] font-bold leading-snug">
                One tap. No app store. Instant vault access.
              </div>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              data-testid="pwa-install-dismiss"
              className="shrink-0 h-8 w-8 rounded-full border-2 border-white/25 text-white/70 hover:text-white hover:bg-white/10 flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={install}
            data-testid="pwa-install-btn"
            className="w-full h-11 bg-[#ffd900] text-[#3a1064] font-black uppercase tracking-[2px] text-sm border-t-2 border-[#3a1064]/40 active:translate-y-[1px] flex items-center justify-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isIOS() && !deferred ? 'Show me how' : 'Install now'}
          </button>
        </div>
      </div>

      {showIOSHelp && (
        <div
          className="fixed inset-0 z-[90] bg-black/70 flex items-end sm:items-center justify-center p-3"
          onClick={dismiss}
          data-testid="pwa-ios-help-overlay"
        >
          <div
            className="w-full max-w-md rounded-2xl border-[3px] border-[#ffd900] p-5 relative"
            style={{
              background:
                'linear-gradient(135deg, #3a1064 0%, #1a0440 60%, #0b0320 100%)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={dismiss}
              aria-label="Close"
              className="absolute top-2 right-2 h-8 w-8 rounded-full border-2 border-white/25 text-white/80 flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <img
                src="/icons/icon-192.png"
                alt="Sprite Vault"
                className="h-14 w-14 rounded-xl border-2 border-[#ffd900]"
              />
              <div>
                <div className="text-[10px] font-black uppercase tracking-[2px] text-[#ffd900]">
                  Add to Home Screen
                </div>
                <div className="text-white font-black text-lg leading-tight">
                  Two taps in Safari
                </div>
              </div>
            </div>

            <ol className="space-y-2 mt-2">
              <li className="flex items-start gap-3 text-white">
                <span className="h-6 w-6 rounded-full bg-[#ffd900] text-[#3a1064] font-black flex items-center justify-center text-xs shrink-0">1</span>
                <span className="font-bold text-sm">
                  Tap the <Share className="inline h-4 w-4 mx-1 -mt-[2px]" /> <b>Share</b> icon at the bottom of Safari.
                </span>
              </li>
              <li className="flex items-start gap-3 text-white">
                <span className="h-6 w-6 rounded-full bg-[#ffd900] text-[#3a1064] font-black flex items-center justify-center text-xs shrink-0">2</span>
                <span className="font-bold text-sm">
                  Scroll and choose <b>&ldquo;Add to Home Screen&rdquo;</b>, then tap <b>Add</b>.
                </span>
              </li>
            </ol>

            <div className="mt-4 rounded-xl bg-black/30 border border-white/10 p-3 text-[11px] text-white/70 font-bold">
              You&apos;ll get a Sprite Vault icon on your home screen that opens instantly — no browser bar, feels like a real app.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
